import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  // Tooltip, // Kullanılmıyor
  IconButton,
  Stack,
  Button,
  Chip,
  // TextField, // Kullanılmıyor
  LinearProgress,
  Switch,
  FormControlLabel,
  Alert,
  Slider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ReferenceLine,
  Brush,
  Label
} from 'recharts';
// import RefreshIcon from '@mui/icons-material/Refresh'; // Kullanılmıyor
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BugReportIcon from '@mui/icons-material/BugReport';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ErrorIcon from '@mui/icons-material/Error'; // ErrorIcon import edildi
import AnimatedNumber from '../../components/common/AnimatedNumber';

// Renk paleti
const COLORS = ['#4caf50', '#ff9800', '#f44336', '#3f51b5', '#9c27b0', '#e91e63', '#2196f3', '#00bcd4'];

// Örnek veri setleri
const generateTimeSeries = (days = 90, withAnomalies = true) => {
  const data = [];
  let baseValue = 500;
  const now = new Date();
  
  // Günlük periyodik dalgalanmalar (haftalık trend)
  for (let i = days; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    // Haftalık periyodik değişiklikler
    let dayOfWeek = date.getDay();
    let weekFactor = dayOfWeek === 0 || dayOfWeek === 6 ? 0.7 : 1.0; // Hafta sonu düşüş
    
    // Saatlik dalgalanmalar
    let hourlyVariation = Array.from({length: 24}, (_, idx) => {
      // Sabah 9-17 arası en yüksek değerler
      let hourFactor = (idx >= 9 && idx <= 17) ? 1.0 + (0.5 * Math.sin((idx - 9) * Math.PI / 8)) : 0.6;
      return baseValue * weekFactor * hourFactor + Math.random() * 50 - 25;
    });
    
    // Rastgele dalgalanmalarla temel değeri modifiye et
    let dailyValue = baseValue + Math.random() * 80 - 40; 
    dailyValue *= weekFactor;
    
    // Belirli bir trend ekle (artan eğilim)
    dailyValue += i * 0.5;
    
    // Anomalileri ekle
    let hasAnomaly = false;
    let anomalyDescription = "";
    
    if (withAnomalies) {
      // Yaklaşık %5 oranında anomali ekle
      if (Math.random() < 0.05) {
        // Keskin anomaliler
        const anomalyType = Math.random();
        if (anomalyType < 0.5) {
          // Ani yükseliş
          dailyValue *= 1.5 + Math.random() * 1.5;
          hasAnomaly = true;
          anomalyDescription = "Ani Artış";
        } else {
          // Ani düşüş
          dailyValue *= 0.3 + Math.random() * 0.3;
          hasAnomaly = true;
          anomalyDescription = "Ani Düşüş";
        }
      }
      
      // Son 7 günde artan trend anomalisi
      if (i < 7 && i > 0) {
        dailyValue *= (1 + (7 - i) * 0.03);
      }
    }
    
    data.push({
      date: date.toISOString().split('T')[0],
      value: Math.round(dailyValue),
      hourly: hourlyVariation,
      hasAnomaly,
      anomalyDescription
    });
  }
  
  return data;
};

// Çoklu metrik için gerçekçi veri üreten fonksiyon
const generateMultiMetricData = (days = 90) => {
  const metrics = [
    { 
      id: 'cpu', 
      name: 'CPU Kullanımı', 
      unit: '%', 
      baseValue: 30, 
      variance: 15, 
      anomalyThresholds: { low: 10, high: 85 },
      anomalyDays: [5, 18, 35, 72]
    },
    { 
      id: 'memory', 
      name: 'RAM Kullanımı', 
      unit: '%', 
      baseValue: 40, 
      variance: 20, 
      anomalyThresholds: { low: 15, high: 90 },
      anomalyDays: [8, 25, 60]
    },
    { 
      id: 'disk_io', 
      name: 'Disk I/O', 
      unit: 'MB/s', 
      baseValue: 12, 
      variance: 8, 
      anomalyThresholds: { low: 0.5, high: 35 },
      anomalyDays: [12, 40, 65]
    },
    { 
      id: 'network', 
      name: 'Ağ Trafiği', 
      unit: 'MB/s', 
      baseValue: 20, 
      variance: 15, 
      anomalyThresholds: { low: 2, high: 60 },
      anomalyDays: [15, 32, 70]
    },
    { 
      id: 'response_time', 
      name: 'Yanıt Süresi', 
      unit: 'ms', 
      baseValue: 150, 
      variance: 80, 
      anomalyThresholds: { low: 50, high: 500 },
      anomalyDays: [7, 28, 45, 75]
    }
  ];
  
  // Son n gün için günlük veriler oluştur
  const now = new Date();
  let allData = [];
  
  metrics.forEach(metric => {
    const metricData = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Periyodik değişimler ekle (haftalık dalgalanma)
      const dayOfWeek = date.getDay();
      const weekendFactor = dayOfWeek === 0 || dayOfWeek === 6 ? 0.7 : 1.1;
      
      // Temel değer
      let value = metric.baseValue + (Math.random() * 2 - 1) * metric.variance;
      value *= weekendFactor;
      
      // Artan trend ekle
      value += (days - i) * 0.05;
      
      // Anomali ekleyelim
      let hasAnomaly = false;
      let anomalyType = '';
      let expected = value;
      
      // Önceden belirlenmiş günlerde anomali oluştur
      if (metric.anomalyDays.includes(i)) {
        hasAnomaly = true;
        
        // Anomali tipi
        const anomalyFactor = Math.random();
        
        if (anomalyFactor < 0.5) {
          // Ani yükseliş - değeri 2-3 kat arttır
          value *= 2 + Math.random();
          anomalyType = 'SPIKE';
        } else {
          // Ani düşüş - değeri %20-50 azalt
          value *= 0.2 + Math.random() * 0.3;
          anomalyType = 'DROP';
        }
      }
      
      metricData.push({
        date: dateStr,
        metricId: metric.id,
        metricName: metric.name,
        value: Math.round(value * 100) / 100,
        unit: metric.unit,
        expected: Math.round(expected * 100) / 100,
        hasAnomaly,
        anomalyType,
        threshold: hasAnomaly ? 
          (anomalyType === 'SPIKE' ? metric.anomalyThresholds.high : metric.anomalyThresholds.low) : 
          null
      });
    }
    
    allData = [...allData, ...metricData];
  });
  
  return allData;
};

// Bugün için canlı metrik verileri
const generateLiveMetrics = () => {
  return [
    { id: 'cpu', name: 'CPU Kullanımı', value: 42, unit: '%', status: 'normal' },
    { id: 'memory', name: 'RAM Kullanımı', value: 68, unit: '%', status: 'normal' },
    { id: 'disk_io', name: 'Disk I/O', value: 25, unit: 'MB/s', status: 'normal' },
    { id: 'network', name: 'Ağ Trafiği', value: 18, unit: 'MB/s', status: 'normal' },
    { id: 'response_time', name: 'Yanıt Süresi', value: 230, unit: 'ms', status: 'warning' }
  ];
};

// Anomali tipleri için sabitler
const ANOMALY_TYPES = {
  SPIKE: { label: 'Ani Artış', color: '#f44336', icon: <TrendingUpIcon /> },
  DROP: { label: 'Ani Düşüş', color: '#f44336', icon: <TrendingDownIcon /> },
  TREND: { label: 'Trend Değişimi', color: '#ff9800', icon: <ShowChartIcon /> },
  PATTERN: { label: 'Desen Değişimi', color: '#9c27b0', icon: <ShuffleIcon /> }
};

function TrendingUpIcon() {
  return <Box component="span" sx={{ color: '#f44336', fontSize: '1.2rem' }}>↑</Box>;
}

function TrendingDownIcon() {
  return <Box component="span" sx={{ color: '#f44336', fontSize: '1.2rem' }}>↓</Box>;
}

function ShowChartIcon() {
  return <Box component="span" sx={{ color: '#ff9800', fontSize: '1.2rem' }}>～</Box>;
}

function ShuffleIcon() {
  return <Box component="span" sx={{ color: '#9c27b0', fontSize: '1.2rem' }}>⇄</Box>;
}

// Algoritma performans verileri
const algorithmPerformanceData = [
  { name: 'İstatistiksel', precision: 87, recall: 82, f1: 84, trainingTime: 0.8, complexity: 'Düşük' },
  { name: 'ARIMA', precision: 91, recall: 85, f1: 88, trainingTime: 2.5, complexity: 'Orta' },
  { name: 'İzolasyon Ormanı', precision: 93, recall: 89, f1: 91, trainingTime: 3.2, complexity: 'Orta' },
  { name: 'Derin Öğrenme', precision: 95, recall: 92, f1: 93, trainingTime: 12.5, complexity: 'Yüksek' }
];

// Örnek anomaliler
const mockAnomalies = [
  { id: 1, timestamp: '2023-11-12 08:45', metric: 'CPU Kullanımı', value: 92, expected: 45, severity: 'Yüksek', type: 'SPIKE' },
  { id: 2, timestamp: '2023-11-11 14:23', metric: 'Bellek Kullanımı', value: 95, expected: 60, severity: 'Yüksek', type: 'SPIKE' },
  { id: 3, timestamp: '2023-11-10 11:17', metric: 'Ağ Trafiği', value: 12, expected: 80, severity: 'Orta', type: 'DROP' },
  { id: 4, timestamp: '2023-11-09 23:05', metric: 'Disk I/O', value: 320, expected: 150, severity: 'Düşük', type: 'TREND' },
  { id: 5, timestamp: '2023-11-08 16:37', metric: 'Kullanıcı İşlemleri', value: 25, expected: 120, severity: 'Orta', type: 'DROP' },
  { id: 6, timestamp: '2023-11-07 10:15', metric: 'Sipariş Miktarı', value: 523, expected: 230, severity: 'Yüksek', type: 'SPIKE' },
  { id: 7, timestamp: '2023-11-06 07:22', metric: 'Hata Oranı', value: 15, expected: 2, severity: 'Yüksek', type: 'SPIKE' },
  { id: 8, timestamp: '2023-11-05 20:18', metric: 'API Yanıt Süresi', value: 4500, expected: 250, severity: 'Yüksek', type: 'SPIKE' },
  { id: 9, timestamp: '2023-11-04 15:10', metric: 'Sepet Ortalaması', value: 45, expected: 120, severity: 'Orta', type: 'DROP' }
];

// Özet metrikler
const summaryMetrics = {
  totalAnomalies: 147,
  lastWeekChange: 12.5,
  detectionAccuracy: 92.7,
  falsePositives: 3.2,
  avgResponseTime: 2.5,
  topAlertType: 'Ani Artış',
  monitoredMetrics: 32
};

const AnomaliTespiti = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [multiMetricData, setMultiMetricData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('İzolasyon Ormanı');
  const [timeWindow, setTimeWindow] = useState(30);
  const [anomalies, setAnomalies] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('Tümü');
  const [sensitivityLevel, setSensitivityLevel] = useState(80);
  const [isRealtime, setIsRealtime] = useState(false);
  const [showExpected, setShowExpected] = useState(true);
  const [detectionThreshold, setDetectionThreshold] = useState([70, 95]);
  const [liveMetrics, setLiveMetrics] = useState([]);
  const [activeMetric, setActiveMetric] = useState('all');
  const [liveMonitoring, setLiveMonitoring] = useState(false);
  const [anomalyDetected, setAnomalyDetected] = useState(false);
  const [lastAnomalyTime, setLastAnomalyTime] = useState(null);
  
  // Veri yükleme simülasyonu
  useEffect(() => {
    setLoading(true);
    
    // Simüle edilmiş yükleme gecikmesi
    setTimeout(() => {
      const timeSeriesData = generateTimeSeries(90, true);
      const multiData = generateMultiMetricData(90);
      const liveData = generateLiveMetrics();
      
      setTimeSeriesData(timeSeriesData);
      setMultiMetricData(multiData);
      setLiveMetrics(liveData);
      setAnomalies(mockAnomalies);
      setLoading(false);
    }, 1000);
  }, []);
  
  // Canlı izleme simulasyonu
  useEffect(() => {
    let interval;
    
    if (liveMonitoring) {
      interval = setInterval(() => {
        // Yeni canlı metrikler oluştur
        const newLiveMetrics = liveMetrics.map(metric => {
          // Rastgele değer değişimi
          let newValue = metric.value + (Math.random() * 10 - 5);
          
          // Min-max sınırları
          if (metric.id === 'cpu') {
            newValue = Math.max(1, Math.min(100, newValue));
          } else if (metric.id === 'memory') {
            newValue = Math.max(5, Math.min(98, newValue));
          } else if (metric.id === 'disk_io') {
            newValue = Math.max(0.1, Math.min(50, newValue));
          } else if (metric.id === 'network') {
            newValue = Math.max(0.5, Math.min(80, newValue));
          } else if (metric.id === 'response_time') {
            newValue = Math.max(50, Math.min(800, newValue));
          }
          
          // Durum kontrol
          let status = 'normal';
          if (metric.id === 'cpu' && newValue > 80) status = 'warning';
          if (metric.id === 'memory' && newValue > 85) status = 'warning';
          if (metric.id === 'response_time' && newValue > 300) status = 'warning';
          
          // Rastgele anomali
          if (Math.random() < 0.03) {
            if (metric.id === 'cpu') newValue = 95 + Math.random() * 5;
            if (metric.id === 'memory') newValue = 96 + Math.random() * 4;
            if (metric.id === 'response_time') newValue = 600 + Math.random() * 500;
            status = 'anomaly';
            
            // Anomali bildirimi
            setAnomalyDetected(true);
            setLastAnomalyTime(new Date().toLocaleTimeString());
            
            // 3 saniye sonra bildirimi kapat
            setTimeout(() => {
              setAnomalyDetected(false);
            }, 3000);
          }
          
          return {
            ...metric,
            value: Math.round(newValue * 100) / 100,
            status
          };
        });
        
        setLiveMetrics(newLiveMetrics);
      }, 2000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [liveMonitoring, liveMetrics]);
  
  // Tab değişimi işleyicisi
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Algoritma değişikliği işleyicisi
  const handleAlgorithmChange = (event) => {
    setSelectedAlgorithm(event.target.value);
  };
  
  // Zaman penceresi değişikliği işleyicisi
  const handleTimeWindowChange = (event) => {
    setTimeWindow(event.target.value);
  };
  
  // Metrik filtresi değişikliği - anomali yönetimi sekmesi için
  const handleMetricFilterChange = (event) => {
    setSelectedMetric(event.target.value);
  };
  
  // Hassasiyet seviyesi değişikliği
  const handleSensitivityChange = (event, newValue) => {
    setSensitivityLevel(newValue);
  };
  
  // Eşik değeri değişikliği
  const handleThresholdChange = (event, newValue) => {
    setDetectionThreshold(newValue);
  };
  
  // Metriğe göre veri filtrele
  const getMetricData = (metricId) => {
    if (metricId === 'all') {
      return multiMetricData;
    }
    return multiMetricData.filter(item => item.metricId === metricId);
  };
  
  // Aktif metriği değiştir - çoklu metrik izleme sekmesi için
  const handleActiveMetricChange = (metricId) => {
    setActiveMetric(metricId);
  };
  
  // Canlı izlemeyi başlat/durdur
  const toggleLiveMonitoring = () => {
    setLiveMonitoring(!liveMonitoring);
  };
  
  // Durum rengini al
  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return '#4caf50';
      case 'warning': return '#ff9800';
      case 'anomaly': return '#f44336';
      default: return '#757575';
    }
  };
  
  // Metriğe göre anomalileri filtrele
  const filteredAnomalies = selectedMetric === 'Tümü' 
    ? anomalies 
    : anomalies.filter(a => a.metric === selectedMetric);
  
  // Son x gün verilerini filtreleme
  const filteredTimeSeriesData = timeWindow === 'all' 
    ? timeSeriesData 
    : timeSeriesData.slice(-timeWindow);
  
  // Anomali bulunma rengi
  const getAnomalyColor = (hasAnomaly) => {
    return hasAnomaly ? '#f44336' : '#4caf50';
  };
  
  // Veri nokta renkleri (anomali varlığına göre)
  const renderColorfulPoint = (entry, index) => {
    return <circle cx={0} cy={0} r={4} fill={getAnomalyColor(entry.hasAnomaly)} />;
  };
  
  // Özel anomali etiketi bileşeni
  // const CustomizedLabel = (props) => {
  //   const { x, y, value, hasAnomaly } = props;
  //   if (!hasAnomaly) return null;
    
  //   return (
  //     <g>
  //       <circle cx={x} cy={y} r={6} fill={getAnomalyColor(hasAnomaly)} />
  //       <circle cx={x} cy={y} r={10} fill="transparent" stroke={getAnomalyColor(hasAnomaly)} strokeWidth={1} />
  //     </g>
  //   );
  // };
  
  // Anomali şiddetine göre renk
  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'yüksek': return '#f44336';
      case 'orta': return '#ff9800';
      case 'düşük': return '#2196f3';
      default: return '#757575';
    }
  };
  
  // Anomali tipine göre simge
  const getAnomalyTypeIcon = (type) => {
    return ANOMALY_TYPES[type]?.icon || <WarningIcon />;
  };

  // Çoklu Metrik İzleme Sekmesi
  const renderMultiMetricTab = () => (
    <Grid container spacing={4}>
      <Grid item xs={12} md={9}>
        <Paper 
          sx={{ 
            p: 3, 
            borderRadius: 2, 
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '4px',
              background: 'linear-gradient(90deg, #3f51b5, #2196f3)',
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">
                {activeMetric === 'all' ? 'Tüm Metrikler' : multiMetricData.find(m => m.metricId === activeMetric)?.metricName || 'Metrik'} Analizi
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Son {timeWindow === 'all' ? '90' : timeWindow} gün için metrik verileri ve anomaliler
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel>Metrik</InputLabel>
                <Select
                  value={activeMetric}
                  label="Metrik"
                  onChange={(e) => handleActiveMetricChange(e.target.value)}
                >
                  <MenuItem value="all">Tüm Metrikler</MenuItem>
                  <MenuItem value="cpu">CPU Kullanımı</MenuItem>
                  <MenuItem value="memory">RAM Kullanımı</MenuItem>
                  <MenuItem value="disk_io">Disk I/O</MenuItem>
                  <MenuItem value="network">Ağ Trafiği</MenuItem>
                  <MenuItem value="response_time">Yanıt Süresi</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Zaman Aralığı</InputLabel>
                <Select
                  value={timeWindow}
                  label="Zaman Aralığı"
                  onChange={handleTimeWindowChange}
                >
                  <MenuItem value={7}>Son 7 gün</MenuItem>
                  <MenuItem value={14}>Son 14 gün</MenuItem>
                  <MenuItem value={30}>Son 30 gün</MenuItem>
                  <MenuItem value={60}>Son 60 gün</MenuItem>
                  <MenuItem value={90}>Son 90 gün</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>
          
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={getMetricData(activeMetric).slice(-timeWindow)}
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => {
                    const d = new Date(date);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  }} 
                />
                <YAxis />
                <RechartsTooltip 
                  formatter={(value, name, props) => {
                    const item = props.payload;
                    if (name === 'value') {
                      return [`${value} ${item.unit}`, 'Gerçek Değer'];
                    }
                    if (name === 'expected') {
                      return [`${value} ${item.unit}`, 'Beklenen Değer'];
                    }
                    return [value, name];
                  }}
                  labelFormatter={(label) => `Tarih: ${label}`}
                />
                <Legend />
                
                {activeMetric === 'all' ? (
                  // Tüm metrikleri göster
                  ['cpu', 'memory', 'disk_io', 'network', 'response_time'].map((metricId, index) => (
                    <Line 
                      key={metricId}
                      type="monotone" 
                      dataKey="value" 
                      data={getMetricData(metricId).slice(-timeWindow)}
                      name={getMetricData(metricId)[0]?.metricName || metricId}
                      stroke={COLORS[index % COLORS.length]}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        return payload.hasAnomaly ? (
                          <circle 
                            cx={cx} 
                            cy={cy} 
                            r={6} 
                            fill="#f44336" 
                            stroke="#fff" 
                            strokeWidth={1} 
                          />
                        ) : (
                          <circle 
                            cx={cx} 
                            cy={cy} 
                            r={3} 
                            fill={COLORS[index % COLORS.length]} 
                          />
                        );
                      }}
                    />
                  ))
                ) : (
                  // Sadece seçili metriği göster
                  <>
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#3f51b5" 
                      strokeWidth={2}
                      name="Gerçek Değer"
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        return payload.hasAnomaly ? (
                          <circle 
                            cx={cx} 
                            cy={cy} 
                            r={6} 
                            fill="#f44336" 
                            stroke="#fff" 
                            strokeWidth={1} 
                          />
                        ) : (
                          <circle 
                            cx={cx} 
                            cy={cy} 
                            r={3} 
                            fill="#3f51b5" 
                          />
                        );
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="expected" 
                      stroke="#4caf50" 
                      strokeDasharray="3 3"
                      name="Beklenen Değer"
                      dot={false}
                    />
                    {/* Eşik çizgisi */}
                    {getMetricData(activeMetric).some(item => item.threshold) && (
                      <ReferenceLine 
                        y={getMetricData(activeMetric).find(item => item.threshold)?.threshold}
                        stroke="#f44336"
                        strokeDasharray="3 3"
                        label={{
                          value: 'Anomali Eşiği',
                          position: 'right',
                          fill: '#f44336',
                          fontSize: 12
                        }}
                      />
                    )}
                  </>
                )}
                <Brush dataKey="date" height={30} stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
          </Box>
          
          {activeMetric !== 'all' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {activeMetric === 'cpu' && 'CPU Kullanımı Analizi'}
                {activeMetric === 'memory' && 'RAM Kullanımı Analizi'}
                {activeMetric === 'disk_io' && 'Disk I/O Analizi'}
                {activeMetric === 'network' && 'Ağ Trafiği Analizi'}
                {activeMetric === 'response_time' && 'Yanıt Süresi Analizi'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeMetric === 'cpu' && 'CPU kullanımındaki ani artışlar, yüksek işlem yükü veya görev sıkışması gibi durumları gösterir. %80 üzerinde kullanım genellikle performans sorunlarına işaret eder.'}
                {activeMetric === 'memory' && 'Bellek kullanımındaki ani artışlar, bellek sızıntıları veya kaynak açlığı olabilir. %90 üzerinde kullanım kritik sistem performansını etkileyebilir.'}
                {activeMetric === 'disk_io' && 'Disk I/O aktivitesindeki ani artışlar, yoğun veri işlemleri veya disk sıkışması olduğunu gösterebilir. Sürekli yüksek I/O sistem performansını düşürür.'}
                {activeMetric === 'network' && 'Ağ trafiğindeki ani artışlar, olağandışı ağ aktivitesi, saldırı veya veri sızıntısı olabilir. Sürekli yüksek trafik bant genişliği sorunlarına işaret eder.'}
                {activeMetric === 'response_time' && 'Yanıt süresindeki ani artışlar, sistem performans sorunları, veri tabanı gecikmesi veya ağ tıkanıklığı olabilir. 300ms üzeri kullanıcı deneyimini olumsuz etkiler.'}
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={3}>
        <Stack spacing={3}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Canlı Metrikler</Typography>
              <Button
                variant={liveMonitoring ? "contained" : "outlined"}
                color={liveMonitoring ? "success" : "primary"}
                startIcon={liveMonitoring ? <PauseIcon /> : <PlayArrowIcon />}
                onClick={toggleLiveMonitoring}
                size="small"
              >
                {liveMonitoring ? "Durdur" : "Başlat"}
              </Button>
            </Box>
            
            {anomalyDetected && (
              <Alert severity="error" sx={{ mb: 2, animation: 'pulse 1s infinite' }}>
                <Typography variant="body2">
                  <strong>Anomali Tespit Edildi!</strong> - {lastAnomalyTime}
                </Typography>
              </Alert>
            )}
            
            <Stack spacing={2}>
              {liveMetrics.map(metric => (
                <Box 
                  key={metric.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: metric.status === 'anomaly' ? 'error.lighter' : 'transparent',
                    transition: 'all 0.3s'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {metric.name}
                    </Typography>
                    <Chip 
                      label={metric.status === 'anomaly' ? 'Anomali' : metric.status === 'warning' ? 'Uyarı' : 'Normal'} 
                      size="small"
                      color={
                        metric.status === 'anomaly' ? 'error' : 
                        metric.status === 'warning' ? 'warning' : 
                        'success'
                      }
                      sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0.1 } }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flexGrow: 1, mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={
                          metric.id === 'cpu' || metric.id === 'memory' ? 
                            metric.value : 
                            metric.id === 'disk_io' ? 
                              (metric.value / 50) * 100 : 
                              metric.id === 'network' ? 
                                (metric.value / 80) * 100 : 
                                (metric.value / 500) * 100
                        } 
                        sx={{ 
                          height: 8, 
                          borderRadius: 1,
                          bgcolor: 'rgba(0,0,0,0.05)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getStatusColor(metric.status)
                          }
                        }}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight="bold" color={getStatusColor(metric.status)}>
                      {metric.value} {metric.unit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
          
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Anomali İstatistikleri</Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'error.main' }}>
                    <WarningIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText 
                  primary="Son 30 günde tespit edilen" 
                  secondary={`${multiMetricData.filter(d => d.hasAnomaly && new Date(d.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)).length} anomali`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'warning.main' }}>
                    <ErrorIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText 
                  primary="En çok anomali görülen metrik" 
                  secondary="Yanıt Süresi (17 anomali)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'info.main' }}>
                    <AccessTimeIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText 
                  primary="Ortalama yanıt süresi" 
                  secondary="45 saniye"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'success.main' }}>
                    <CheckCircleIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText 
                  primary="Tespit doğruluğu" 
                  secondary="%96.3 (son 90 gün)"
                />
              </ListItem>
            </List>
          </Paper>
        </Stack>
      </Grid>
      
      <Grid item xs={12}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Son Tespit Edilen Anomaliler
          </Typography>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Metrik</TableCell>
                  <TableCell align="right">Değer</TableCell>
                  <TableCell align="right">Beklenen</TableCell>
                  <TableCell>Anomali Tipi</TableCell>
                  <TableCell>Olası Nedeni</TableCell>
                  <TableCell align="right">Tepki Süresi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {multiMetricData
                  .filter(item => item.hasAnomaly)
                  .slice(-10)
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((anomaly, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{anomaly.date}</TableCell>
                      <TableCell>{anomaly.metricName}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        {anomaly.value} {anomaly.unit}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>
                        {anomaly.expected} {anomaly.unit}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={anomaly.anomalyType === 'SPIKE' ? 'Ani Artış' : 'Ani Düşüş'} 
                          size="small"
                          color={anomaly.anomalyType === 'SPIKE' ? 'error' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        {anomaly.metricId === 'cpu' && anomaly.anomalyType === 'SPIKE' && 'Yüksek işlemci yükü'}
                        {anomaly.metricId === 'memory' && anomaly.anomalyType === 'SPIKE' && 'Bellek sızıntısı'}
                        {anomaly.metricId === 'disk_io' && anomaly.anomalyType === 'SPIKE' && 'Yoğun disk aktivitesi'}
                        {anomaly.metricId === 'network' && anomaly.anomalyType === 'SPIKE' && 'Olağandışı ağ trafiği'}
                        {anomaly.metricId === 'response_time' && anomaly.anomalyType === 'SPIKE' && 'Sistem yanıt gecikmesi'}
                        {anomaly.anomalyType === 'DROP' && 'Sistem kaynağında kesinti'}
                      </TableCell>
                      <TableCell align="right">
                        {Math.floor(25 + Math.random() * 35)} sn
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }} className="slide-in-left">
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Anomali Tespiti
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Verilerinizi analiz ederek anormal davranışları ve olağan dışı olayları otomatik olarak tespit edin.
        </Typography>
        <Divider sx={{ my: 3 }} />
      </Box>

      {/* Yükleniyor durumu */}
      {loading && (
        <Box sx={{ width: '100%', my: 4 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Anomali verileri yükleniyor...
          </Typography>
        </Box>
      )}

      {!loading && (
        <>
          {/* Özet Kartlar */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Tespit Edilen Anomaliler
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        <AnimatedNumber 
                          value={summaryMetrics.totalAnomalies} 
                          duration={1500}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        son 30 günde
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#f44336', width: 48, height: 48 }}>
                      <WarningIcon />
                    </Avatar>
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon 
                      color={summaryMetrics.lastWeekChange > 0 ? 'error' : 'success'} 
                      fontSize="small" 
                      sx={{ mr: 0.5 }}
                    />
                    <Typography 
                      variant="body2" 
                      color={summaryMetrics.lastWeekChange > 0 ? 'error.main' : 'success.main'}
                    >
                      %{Math.abs(summaryMetrics.lastWeekChange)} {summaryMetrics.lastWeekChange > 0 ? 'artış' : 'azalış'} (önceki haftaya göre)
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Tespit Doğruluğu
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold" color="primary">
                        <AnimatedNumber 
                          value={summaryMetrics.detectionAccuracy} 
                          suffix="%" 
                          duration={1500}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        %{summaryMetrics.falsePositives} yanlış pozitif
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#4caf50', width: 48, height: 48 }}>
                      <CheckCircleIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Ortalama Yanıt Süresi
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                        <Typography variant="h4" component="div" fontWeight="bold">
                          <AnimatedNumber
                            value={summaryMetrics.avgResponseTime}
                            duration={1500}
                          />
                        </Typography>
                        <Typography variant="body1" sx={{ ml: 0.5 }}>dk</Typography>
                      </Box>
                    </Box>
                    <Avatar sx={{ bgcolor: '#ff9800', width: 48, height: 48 }}>
                      <AccessTimeIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        İzlenen Metrikler
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        <AnimatedNumber
                          value={summaryMetrics.monitoredMetrics}
                          duration={1500}
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {summaryMetrics.topAlertType} en yaygın tip
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#2196f3', width: 48, height: 48 }}>
                      <AssessmentIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Ana sekmelerin oluşturulması */}
          <Paper sx={{ borderRadius: 2, mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                label="Zaman Serisi Analizi" 
                icon={<AnalyticsIcon />} 
                iconPosition="start" 
              />
              <Tab 
                label="Çoklu Metrik İzleme" 
                icon={<AssessmentIcon />} 
                iconPosition="start" 
              />
              <Tab 
                label="Anomali Yönetimi" 
                icon={<BugReportIcon />} 
                iconPosition="start" 
              />
            </Tabs>
          </Paper>
          
          {/* Sekme İçerikleri */}
          {/* Zaman Serisi Analizi Sekmesi */}
          {activeTab === 0 && (
            <Grid container spacing={4}>
              <Grid item xs={12} lg={9}>
                <Paper 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2, 
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: 'linear-gradient(90deg, #f44336, #ff9800)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">
                        Zaman Serisi Anomali Tespiti
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Zaman içinde metrik değeri ve tespit edilen anormallikler
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={showExpected} 
                            onChange={(e) => setShowExpected(e.target.checked)} 
                            color="primary"
                          />
                        }
                        label="Beklenen Değerler"
                        sx={{ mr: 2 }}
                      />
                      <FormControl sx={{ minWidth: 120 }} size="small">
                        <InputLabel>Zaman Aralığı</InputLabel>
                        <Select
                          value={timeWindow}
                          label="Zaman Aralığı"
                          onChange={handleTimeWindowChange}
                        >
                          <MenuItem value={7}>Son 7 gün</MenuItem>
                          <MenuItem value={14}>Son 14 gün</MenuItem>
                          <MenuItem value={30}>Son 30 gün</MenuItem>
                          <MenuItem value={60}>Son 60 gün</MenuItem>
                          <MenuItem value={90}>Son 90 gün</MenuItem>
                          <MenuItem value="all">Tüm veriler</MenuItem>
                        </Select>
                      </FormControl>
                      <IconButton size="small" sx={{ border: '1px solid #ddd' }}>
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Box sx={{ height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={filteredTimeSeriesData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip 
                          labelFormatter={value => `Tarih: ${value}`}
                          formatter={(value, name) => [value, name === 'value' ? 'Gerçek Değer' : 'Beklenen Değer']}
                        />
                        <Legend />
                        <defs>
                          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#8884d8" stopOpacity={0.2}/>
                          </linearGradient>
                        </defs>
                        <Area 
                          type="monotone" 
                          dataKey="value" 
                          name="Gerçek Değer" 
                          stroke="#8884d8" 
                          fillOpacity={1} 
                          fill="url(#colorValue)" 
                          strokeWidth={2}
                          dot={renderColorfulPoint}
                        />
                        {showExpected && (
                          <Line 
                            type="monotone" 
                            dataKey="expected" 
                            name="Beklenen Değer" 
                            stroke="#82ca9d" 
                            strokeDasharray="5 5" 
                            dot={false}
                          />
                        )}
                        {/* Anomali eşik çizgileri */}
                        <ReferenceLine y={800} stroke="red" strokeDasharray="3 3">
                          <Label value="Üst Eşik" position="insideRight" />
                        </ReferenceLine>
                        <ReferenceLine y={200} stroke="orange" strokeDasharray="3 3">
                          <Label value="Alt Eşik" position="insideRight" />
                        </ReferenceLine>
                        <Brush dataKey="date" height={30} stroke="#8884d8" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} lg={3}>
                <Stack spacing={3}>
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Algoritma Seçimi
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Anomali Tespit Algoritması</InputLabel>
                      <Select
                        value={selectedAlgorithm}
                        label="Anomali Tespit Algoritması"
                        onChange={handleAlgorithmChange}
                      >
                        <MenuItem value="İstatistiksel">İstatistiksel</MenuItem>
                        <MenuItem value="ARIMA">ARIMA</MenuItem>
                        <MenuItem value="İzolasyon Ormanı">İzolasyon Ormanı</MenuItem>
                        <MenuItem value="Derin Öğrenme">Derin Öğrenme</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Hassasiyet Seviyesi ({sensitivityLevel})
                    </Typography>
                    <Slider
                      value={sensitivityLevel}
                      min={10}
                      max={100}
                      step={5}
                      onChange={handleSensitivityChange}
                      valueLabelDisplay="auto"
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography variant="body2" color="text.secondary">
                      {selectedAlgorithm} algoritması için performans değerleri:
                    </Typography>
                    
                    <List dense sx={{ mt: 1 }}>
                      {algorithmPerformanceData
                        .filter(alg => alg.name === selectedAlgorithm)
                        .map((alg, idx) => (
                          <React.Fragment key={idx}>
                            <ListItem>
                              <ListItemIcon sx={{ minWidth: 35 }}>
                                <CheckCircleIcon fontSize="small" color="success" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={`Hassasiyet: ${alg.precision}%`}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon sx={{ minWidth: 35 }}>
                                <CheckCircleIcon fontSize="small" color="success" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={`Kesinlik: ${alg.recall}%`}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon sx={{ minWidth: 35 }}>
                                <AccessTimeIcon fontSize="small" color="info" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={`Eğitim Süresi: ${alg.trainingTime} dk`}
                              />
                            </ListItem>
                          </React.Fragment>
                        ))}
                    </List>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          )}
          
          {/* Çoklu Metrik İzleme Sekmesi */}
          {activeTab === 1 && renderMultiMetricTab()}
          
          {/* Anomali Yönetimi Sekmesi */}
          {activeTab === 2 && (
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>Tespit Edilen Anomaliler</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Zaman</TableCell>
                          <TableCell>Metrik</TableCell>
                          <TableCell align="right">Değer</TableCell>
                          <TableCell align="right">Beklenen</TableCell>
                          <TableCell>Tip</TableCell>
                          <TableCell align="center">Şiddet</TableCell>
                          <TableCell align="right">İşlemler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAnomalies.map((anomaly) => (
                          <TableRow key={anomaly.id}>
                            <TableCell>{anomaly.timestamp}</TableCell>
                            <TableCell>{anomaly.metric}</TableCell>
                            <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                              {anomaly.value}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary' }}>
                              {anomaly.expected}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getAnomalyTypeIcon(anomaly.type)}
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                  {ANOMALY_TYPES[anomaly.type]?.label || anomaly.type}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={anomaly.severity} 
                                size="small"
                                sx={{ 
                                  bgcolor: getSeverityColor(anomaly.severity),
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small">
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Tespit Ayarları</Typography>
                  
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Metrik Filtresi</InputLabel>
                    <Select
                      value={selectedMetric}
                      label="Metrik Filtresi"
                      onChange={handleMetricFilterChange}
                    >
                      <MenuItem value="Tümü">Tüm Metrikler</MenuItem>
                      <MenuItem value="CPU Kullanımı">CPU Kullanımı</MenuItem>
                      <MenuItem value="Bellek Kullanımı">Bellek Kullanımı</MenuItem>
                      <MenuItem value="Ağ Trafiği">Ağ Trafiği</MenuItem>
                      <MenuItem value="Disk I/O">Disk I/O</MenuItem>
                      <MenuItem value="API Yanıt Süresi">API Yanıt Süresi</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Typography variant="subtitle2" gutterBottom>Eşik Değerleri</Typography>
                  <Slider
                    value={detectionThreshold}
                    onChange={handleThresholdChange}
                    valueLabelDisplay="auto"
                    min={0}
                    max={100}
                    sx={{ mb: 3 }}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={isRealtime} 
                        onChange={(e) => setIsRealtime(e.target.checked)} 
                      />
                    }
                    label="Gerçek Zamanlı İzleme"
                  />
                  
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      startIcon={isRealtime ? <PauseIcon /> : <PlayArrowIcon />}
                    >
                      {isRealtime ? 'Durdur' : 'Başlat'}
                    </Button>
                    <Button variant="outlined">Ayarları Sıfırla</Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Container>
  );
};

export default AnomaliTespiti;
